import React, { FunctionComponent, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import {
  currencyOptions,
  ICurrencyOption,
  penaltyWaiverTypeOptions,
  TPenaltyWaiverTypeOption,
} from '../../../../constants';
import {
  defaultTextFieldProps,
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../Inputs';
import { capitalizeFirstLetter } from '../../../../helper';
import DarkLoadingButton, { defaultButtonStyles } from '../../Buttons';
import {
  adminCreatePenaltyWaiver,
  TAdminCreatePenaltyWaiver,
} from '../../../../store/V2/PenaltyWaiverSlice/adminApi';
import { IPenaltyWaiver } from '../../../../store/V2/PenaltyWaiverSlice';

interface AddPenaltyWaiverModalProps {
  open: boolean;
  sectionId: string;
  lessonHistoryId?: string;
  zoomRequestId?: string;
  onClose: () => void;
  onSubmitted: (penaltyWaiver?: IPenaltyWaiver) => void;
}

type TState = {
  selectedType: TPenaltyWaiverTypeOption | null;
  selectedCurrency: ICurrencyOption | null;
  selectedAmount: string;
};

const initialState: TState = {
  selectedType: null,
  selectedCurrency: null,
  selectedAmount: '',
};

const AddPenaltyWaiverModal: FunctionComponent<AddPenaltyWaiverModalProps> = ({
  open,
  sectionId,
  lessonHistoryId,
  zoomRequestId,
  onClose,
  onSubmitted,
}) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  const isSubmitDisabled =
    !state.selectedType ||
    !state.selectedCurrency ||
    !state.selectedAmount.trim().length ||
    parseFloat(state.selectedAmount) === 0 ||
    Number.isNaN(parseFloat(state.selectedAmount));

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (
      sectionId &&
      state.selectedType &&
      state.selectedCurrency &&
      state.selectedAmount.trim().length
    ) {
      const apiData: TAdminCreatePenaltyWaiver = {
        sectionId,
        type: state.selectedType.value,
        amount: parseFloat(state.selectedAmount),
        currency: state.selectedCurrency.value,
      };

      if (lessonHistoryId) {
        apiData.lessonHistoryId = lessonHistoryId;
      }

      if (zoomRequestId) {
        apiData.zoomRequestId = zoomRequestId;
      }

      const res: any = await dispatch(adminCreatePenaltyWaiver(apiData));

      onSubmitted(res && res.payload ? res.payload : undefined);
    }

    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Add New Penalty Waiver
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div style={{ height: '6px' }} />
            <FormControl fullWidth>
              <Autocomplete
                size="small"
                value={state.selectedType}
                options={penaltyWaiverTypeOptions}
                popupIcon={TPAutoCompleteArrowIconComponent}
                getOptionKey={(option) => option.value}
                getOptionLabel={(option) =>
                  capitalizeFirstLetter(option.label.toLocaleLowerCase())
                }
                isOptionEqualToValue={(option, value) =>
                  option.label === value.value
                }
                onChange={(event, value) =>
                  updateStateAttributes({ selectedType: value })
                }
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...defaultTextFieldProps}
                    size="small"
                    label="Type *"
                    placeholder="Select"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Autocomplete
                size="small"
                value={state.selectedCurrency}
                options={currencyOptions}
                popupIcon={TPAutoCompleteArrowIconComponent}
                getOptionKey={(option) => option.value}
                isOptionEqualToValue={(option, value) =>
                  option.label === value.value
                }
                onChange={(event, value) =>
                  updateStateAttributes({ selectedCurrency: value })
                }
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...defaultTextFieldProps}
                    size="small"
                    label="Currency *"
                    placeholder="Select"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TPTextField
                {...defaultTextFieldProps}
                type="number"
                size="small"
                label="Amount *"
                placeholder="Enter Here..."
                value={state.selectedAmount}
                onChange={(e) =>
                  updateStateAttributes({ selectedAmount: e.target.value })
                }
              />
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
        <DarkLoadingButton
          disableElevation
          variant="contained"
          color="inherit"
          loading={isLoading}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          sx={{ ...defaultButtonStyles }}
        >
          Add
        </DarkLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddPenaltyWaiverModal;
